import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SignatureCanvas from 'react-signature-canvas';
import axiosInstance from '../api/axiosConfig';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';
import './RegisterBook.css';

const RegisterBook = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const signatureRef = useRef(null);

    const [activeTab, setActiveTab] = useState('create');
    
    // Stage 1: Check-In
    const [formData, setFormData] = useState({
        name: '',
        purpose: '',
        contactNumber: ''
    });

    const [signatureSaved, setSignatureSaved] = useState(false);
    const [signatureData, setSignatureData] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Stage 2: Checked-In Entries
    const [checkedInEntries, setCheckedInEntries] = useState([]);
    const [loadingCheckedIn, setLoadingCheckedIn] = useState(false);
    const [searchCheckedIn, setSearchCheckedIn] = useState('');
    const [currentCheckedInPage, setCurrentCheckedInPage] = useState(1);
    const [totalCheckedInPages, setTotalCheckedInPages] = useState(1);

    // Stage 3: All Entries
    const [allEntries, setAllEntries] = useState([]);
    const [loadingAll, setLoadingAll] = useState(false);
    const [searchAll, setSearchAll] = useState('');
    const [currentAllPage, setCurrentAllPage] = useState(1);
    const [totalAllPages, setTotalAllPages] = useState(1);
    const [selectedEntry, setSelectedEntry] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleClearSignature = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            setSignatureSaved(false);
            setSignatureData(null);
        }
    };

    const handleSaveSignature = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            const signature = signatureRef.current.toDataURL();
            setSignatureData(signature);
            setSignatureSaved(true);
            setError('');
        } else {
            setError('Please provide a signature before saving');
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Name is required');
            return false;
        }
        if (!formData.purpose.trim()) {
            setError('Purpose is required');
            return false;
        }
        if (!formData.contactNumber.trim()) {
            setError('Contact Number is required');
            return false;
        }
        if (!signatureData) {
            setError('Digital signature is required. Please sign and save your signature.');
            return false;
        }
        return true;
    };

    // Stage 1: Check-In
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post(
                '/api/register-book',
                {
                    ...formData,
                    signature: signatureData
                }
            );

            if (response.data.success) {
                setSuccess('Check-in successful!');
                setIsSubmitted(true);
                
                setTimeout(() => {
                    navigate('/purpose-selection');
                }, 2000);
            }
        } catch (err) {
            console.error('Error submitting check-in:', err);
            setError(err.response?.data?.message || 'Failed to check-in. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleNewEntry = () => {
        setFormData({
            name: '',
            purpose: '',
            contactNumber: ''
        });
        setSignatureData(null);
        setSignatureSaved(false);
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
        setIsSubmitted(false);
        setSuccess('');
        setError('');
    };

    // Fetch checked-in entries
    const fetchCheckedInEntries = async (page = 1, search = '') => {
        setLoadingCheckedIn(true);
        try {
            const response = await axiosInstance.get(
                `/api/register-book/checked-in?page=${page}&limit=10&search=${search}`
            );

            if (response.data.success) {
                setCheckedInEntries(response.data.data);
                setTotalCheckedInPages(response.data.totalPages);
                setCurrentCheckedInPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching checked-in entries:', err);
            setError('Failed to load checked-in entries');
        } finally {
            setLoadingCheckedIn(false);
        }
    };

    // Fetch all entries
    const fetchAllEntries = async (page = 1, search = '') => {
        setLoadingAll(true);
        try {
            const response = await axiosInstance.get(
                `/api/register-book?page=${page}&limit=10&search=${search}`
            );

            if (response.data.success) {
                setAllEntries(response.data.data);
                setTotalAllPages(response.data.totalPages);
                setCurrentAllPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching all entries:', err);
            setError('Failed to load entries');
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'checked-in') {
            fetchCheckedInEntries(currentCheckedInPage, searchCheckedIn);
        } else if (activeTab === 'view-all') {
            fetchAllEntries(currentAllPage, searchAll);
        }
    }, [activeTab]);

    const handleSearchCheckedIn = (e) => {
        const value = e.target.value;
        setSearchCheckedIn(value);
        setCurrentCheckedInPage(1);
        fetchCheckedInEntries(1, value);
    };

    const handleSearchAll = (e) => {
        const value = e.target.value;
        setSearchAll(value);
        setCurrentAllPage(1);
        fetchAllEntries(1, value);
    };

    const handleCheckedInPageChange = (newPage) => {
        setCurrentCheckedInPage(newPage);
        fetchCheckedInEntries(newPage, searchCheckedIn);
    };

    const handleAllPageChange = (newPage) => {
        setCurrentAllPage(newPage);
        fetchAllEntries(newPage, searchAll);
    };

    const handleCheckOut = async (entryId) => {
        if (!window.confirm('Are you sure you want to check out this entry?')) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.patch(
                `/api/register-book/${entryId}/checkout`,
                {}
            );

            if (response.data.success) {
                setSuccess('Check-out successful!');
                fetchCheckedInEntries(currentCheckedInPage, searchCheckedIn);
                
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error('Error checking out:', err);
            setError(err.response?.data?.message || 'Failed to check out. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (entry) => {
        setSelectedEntry(entry);
    };

    const handleCloseDetails = () => {
        setSelectedEntry(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="register-book-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="register-book-content">
                <div className="register-book-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="header-text">
                        <h1>Register Book</h1>
                        <p>Visitor and personnel entry/exit tracking</p>
                    </div>
                    <div className="header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                        </div>
                        <button onClick={() => navigate('/purpose-selection')} className="btn-back">
                            ← Back
                        </button>
                    </div>
                </div>

                {/* Three Tabs */}
                <div className="register-book-tabs">
                    <button
                        className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
                        onClick={() => setActiveTab('create')}
                    >
                        Create Entry (Check-In)
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'checked-in' ? 'active' : ''}`}
                        onClick={() => setActiveTab('checked-in')}
                    >
                        Checked-In (Active)
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'view-all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('view-all')}
                    >
                        View All Entries
                    </button>
                </div>

                {/* Global Success/Error Messages */}
                {success && (
                    <div className="alert alert-success">
                        {success}
                    </div>
                )}

                {error && !selectedEntry && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {/* Tab 1: Create Entry (Check-In) */}
                {activeTab === 'create' && (
                    <form onSubmit={handleSubmit} className="register-book-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="name">Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={isSubmitted}
                                    placeholder="Full name of visitor/personnel"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="contactNumber">Contact Number *</label>
                                <input
                                    type="text"
                                    id="contactNumber"
                                    name="contactNumber"
                                    value={formData.contactNumber}
                                    onChange={handleInputChange}
                                    disabled={isSubmitted}
                                    placeholder="Phone number"
                                    required
                                />
                            </div>

                            <div className="form-group form-group-full">
                                <label htmlFor="purpose">Purpose *</label>
                                <textarea
                                    id="purpose"
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={handleInputChange}
                                    disabled={isSubmitted}
                                    placeholder="Purpose of visit"
                                    rows="3"
                                    required
                                />
                            </div>
                        </div>

                        {!isSubmitted && (
                            <div className="signature-section">
                                <label>Digital Signature *</label>
                                <p className="signature-instruction">
                                    Please sign below using your mouse or touch screen. Signature is mandatory at check-in.
                                </p>
                                
                                <div className="signature-canvas-wrapper">
                                    <SignatureCanvas
                                        ref={signatureRef}
                                        canvasProps={{
                                            className: 'signature-canvas',
                                            width: 500,
                                            height: 200
                                        }}
                                    />
                                </div>

                                <div className="signature-buttons">
                                    <button
                                        type="button"
                                        onClick={handleClearSignature}
                                        className="btn btn-secondary"
                                    >
                                        Clear Signature
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveSignature}
                                        className="btn btn-primary"
                                    >
                                        Save Signature
                                    </button>
                                </div>

                                {signatureSaved && (
                                    <div className="signature-saved">
                                        ✓ Signature saved successfully
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="form-actions">
                            {!isSubmitted ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/purpose-selection')}
                                        className="btn btn-secondary"
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Checking In...' : 'Check In'}
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleNewEntry}
                                    className="btn btn-primary"
                                >
                                    Create New Entry
                                </button>
                            )}
                        </div>
                    </form>
                )}

                {/* Tab 2: Checked-In Entries */}
                {activeTab === 'checked-in' && (
                    <div className="view-all-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search checked-in entries..."
                                value={searchCheckedIn}
                                onChange={handleSearchCheckedIn}
                                className="search-input"
                            />
                        </div>

                        {loadingCheckedIn ? (
                            <div className="loading-state">Loading checked-in entries...</div>
                        ) : checkedInEntries.length === 0 ? (
                            <div className="empty-state">
                                <p>No active check-ins found</p>
                                <p className="empty-state-hint">All visitors have checked out or no entries have been created yet.</p>
                            </div>
                        ) : (
                            <>
                                <div className="entries-table-container">
                                    <table className="entries-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Check-In Time</th>
                                                <th>Name</th>
                                                <th>Purpose</th>
                                                <th>Contact Number</th>
                                                <th>Entered By</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {checkedInEntries.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td>{formatDate(entry.date)}</td>
                                                    <td>{formatTime(entry.checkInTime)}</td>
                                                    <td>{entry.name}</td>
                                                    <td className="purpose-cell">{entry.purpose}</td>
                                                    <td>{entry.contactNumber}</td>
                                                    <td>{entry.enteredBy}</td>
                                                    <td>
                                                        <span className="status-badge status-checked-in">
                                                            Checked In
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn-checkout"
                                                            onClick={() => handleCheckOut(entry._id)}
                                                            disabled={loading}
                                                        >
                                                            Check Out
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalCheckedInPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            onClick={() => handleCheckedInPageChange(currentCheckedInPage - 1)}
                                            disabled={currentCheckedInPage === 1}
                                            className="pagination-btn"
                                        >
                                            Previous
                                        </button>
                                        <span className="pagination-info">
                                            Page {currentCheckedInPage} of {totalCheckedInPages}
                                        </span>
                                        <button
                                            onClick={() => handleCheckedInPageChange(currentCheckedInPage + 1)}
                                            disabled={currentCheckedInPage === totalCheckedInPages}
                                            className="pagination-btn"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Tab 3: View All Entries */}
                {activeTab === 'view-all' && (
                    <div className="view-all-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search all entries..."
                                value={searchAll}
                                onChange={handleSearchAll}
                                className="search-input"
                            />
                        </div>

                        {loadingAll ? (
                            <div className="loading-state">Loading entries...</div>
                        ) : allEntries.length === 0 ? (
                            <div className="empty-state">
                                <p>No entries found</p>
                            </div>
                        ) : (
                            <>
                                <div className="entries-table-container">
                                    <table className="entries-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Check-In Time</th>
                                                <th>Check-Out Time</th>
                                                <th>Name</th>
                                                <th>Purpose</th>
                                                <th>Person to Meet</th>
                                                <th>Contact Number</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allEntries.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td>{formatDate(entry.date)}</td>
                                                    <td>{formatTime(entry.checkInTime)}</td>
                                                    <td>{formatTime(entry.checkOutTime)}</td>
                                                    <td>{entry.name}</td>
                                                    <td className="purpose-cell">{entry.purpose}</td>
                                                    <td>{entry.personToMeet || '-'}</td>
                                                    <td>{entry.contactNumber}</td>
                                                    <td>
                                                        <span className={`status-badge ${entry.status === 'CHECKED_IN' ? 'status-checked-in' : 'status-checked-out'}`}>
                                                            {entry.status === 'CHECKED_IN' ? 'Checked In' : 'Checked Out'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn-view-details"
                                                            onClick={() => handleViewDetails(entry)}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalAllPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            onClick={() => handleAllPageChange(currentAllPage - 1)}
                                            disabled={currentAllPage === 1}
                                            className="pagination-btn"
                                        >
                                            Previous
                                        </button>
                                        <span className="pagination-info">
                                            Page {currentAllPage} of {totalAllPages}
                                        </span>
                                        <button
                                            onClick={() => handleAllPageChange(currentAllPage + 1)}
                                            disabled={currentAllPage === totalAllPages}
                                            className="pagination-btn"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Details Modal */}
                {selectedEntry && (
                    <div className="modal-overlay" onClick={handleCloseDetails}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Entry Details</h2>
                                <button className="modal-close" onClick={handleCloseDetails}>×</button>
                            </div>
                            <div className="modal-body">
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Date:</label>
                                        <span>{formatDate(selectedEntry.date)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Check-In Time:</label>
                                        <span>{formatTime(selectedEntry.checkInTime)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Check-Out Time:</label>
                                        <span>{formatTime(selectedEntry.checkOutTime)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Status:</label>
                                        <span className={`status-badge ${selectedEntry.status === 'CHECKED_IN' ? 'status-checked-in' : 'status-checked-out'}`}>
                                            {selectedEntry.status === 'CHECKED_IN' ? 'Checked In' : 'Checked Out'}
                                        </span>
                                    </div>
                                    <div className="detail-item detail-item-full">
                                        <label>Name:</label>
                                        <span>{selectedEntry.name}</span>
                                    </div>
                                    <div className="detail-item detail-item-full">
                                        <label>Purpose:</label>
                                        <span>{selectedEntry.purpose}</span>
                                    </div>
                                    {selectedEntry.personToMeet && (
                                        <div className="detail-item detail-item-full">
                                            <label>Person to Meet:</label>
                                            <span>{selectedEntry.personToMeet}</span>
                                        </div>
                                    )}
                                    {selectedEntry.notes && (
                                        <div className="detail-item detail-item-full">
                                            <label>Notes:</label>
                                            <span>{selectedEntry.notes}</span>
                                        </div>
                                    )}
                                    <div className="detail-item">
                                        <label>Contact Number:</label>
                                        <span>{selectedEntry.contactNumber}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Entered By:</label>
                                        <span>{selectedEntry.enteredBy}</span>
                                    </div>
                                </div>

                                <div className="signature-detail">
                                    <label>Digital Signature:</label>
                                    <div className="signature-image-container">
                                        <img 
                                            src={selectedEntry.signature} 
                                            alt="Signature" 
                                            className="signature-image"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button onClick={handleCloseDetails} className="btn btn-secondary">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegisterBook;
